import React from 'react';
import { Box, Typography, Accordion, Slider, Badge, Loader, Row, Col } from '@lucid/lego';
import { formatDate } from '../../../../../utils/tools';
import type { FseCase } from '../utils';
import { useGetFSECaseById } from '../../../../../api/services/lupp/fse/fse';
import Comments from '../../../../../components/Comments/Comments';

interface DetailsProps {
  theme: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
  caseFSEDetail: FseCase | null;
  caseName: string;
}

const Details: React.FC<DetailsProps> = ({ theme, isOpen, onClose, caseFSEDetail, caseName }) => {
  const caseId = String(caseFSEDetail?.techSupportIdentifier || '');
  const {
    data: caseDetail,
    isLoading,
    error,
  } = useGetFSECaseById(caseId, !!caseFSEDetail?.techSupportIdentifier && isOpen);

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Slider
      isOpen={isOpen}
      onClose={onClose}
      title={caseName}
      width="1000px"
      direction="right"
      theme={invertedTheme}
    >
      {isLoading ? (
        <Box pt={48} pb={48} className="fetchingOverlay">
          <Loader size={32} />
        </Box>
      ) : error ? (
        <Box pt={48} pb={48}>
          <Typography variant="body" size="large" theme={invertedTheme} color="error-1">
            Failed to load case details. Please try again later.
          </Typography>
        </Box>
      ) : caseDetail ? (
        <Row>
          {/* Case Information */}
          <Col sm={12} md={12}>
            <Accordion
              title="Case Information"
              theme={invertedTheme}
              defaultOpen={true}
              showBorder={true}
            >
              <Row gutter={[8, 0]}>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Status
                  </Typography>
                  <Box mt={4}>
                    <Badge
                      label={caseDetail?.status || 'N/A'}
                      color={caseDetail?.status === 'Open' ? 'error' : 'success'}
                      type="secondary"
                      theme={invertedTheme}
                    />
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Priority
                  </Typography>
                  <Box mt={4}>
                    <Badge
                      label={caseDetail?.priority || 'N/A'}
                      color={caseDetail?.priority === 'High' ? 'error' : 'informative'}
                      type="secondary"
                      theme={invertedTheme}
                    />
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Owner
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.ownerName || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Assigned Technician
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.technicianName || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Support Type
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.escalationType || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Est. Resolution
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {formatDate(caseDetail?.createdDatetime || 'N/A')}
                    </Typography>
                  </Box>
                </Col>
              </Row>
            </Accordion>
          </Col>
          {/* Vehicle Information */}
          <Col sm={12} md={12}>
            <Accordion
              title="Vehicle Information"
              theme={invertedTheme}
              defaultOpen={true}
              showBorder={true}
            >
              <Row gutter={[8, 0]}>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    VIN
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.vin || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Model
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.vehicleDetails?.Model || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Customer
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.customerName || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Mileage In
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.mileageInMiles || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
                <Col xl={6} lg={6}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Mileage Out
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseDetail?.mileageOutMiles || 'N/A'}
                    </Typography>
                  </Box>
                </Col>
              </Row>
            </Accordion>
          </Col>

          {/* Comments Section */}
          <Col sm={12} md={12}>
            <Accordion title="Comments" theme={invertedTheme} defaultOpen={true} showBorder={true}>
              <Comments
                entityId={caseId}
                entityType="FSE_TECH_SUPPORT"
                theme={invertedTheme}
                enabled={isOpen}
                gaComponent="fse-detail-post-comment"
              />
            </Accordion>
          </Col>
        </Row>
      ) : null}
    </Slider>
  );
};

export default Details;
